import Skeleton from "@components/skeleton/Skeleton";
import { withTheme } from "@hoc/withTheme";
import { roundToDecimalPlaces } from "@utils/general";
import { getTextWidthByElement } from "@utils/string";
import { transparentize } from "polished";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { DefaultTheme } from "styled-components";

import TestIds from "../../testIds";
import { PropsWithTheme } from "../../theme";
import { Description, ProgressValue, ProgressWrapper, StyledCanvas, StyledProgressBar } from "./ProgressBar.styles";
import { ProgressBarColor } from "./ProgressBar.utils";

export type TProgressBarDescFormatter = (value: number, parts: number) => string | React.ReactElement;

export interface IProps {
    value: number;
    parts: number;
    showMarks?: boolean;
    /** Displayed number of parts in description is value subtracted from parts. */
    isDescriptionInverseValue?: boolean;
    /** Changes the color scheme */
    color?: ProgressBarColor | keyof DefaultTheme;
    /** Custom string before the value/of part */
    customDescription?: string;
    /** Fully custom description */
    descriptionFormatter?: TProgressBarDescFormatter;
    descriptionFlexDirection?: "row" | "column";
    style?: React.CSSProperties;
    className?: string;
    /** Custom description formatter */
    hideNumbers?: boolean;
    hideDescription?: boolean;
    isSkeleton?: boolean;
    descWidthRatio?: number;
    animateEmpty?: boolean;
}

interface IPropsExtended extends IProps, PropsWithTheme, WithTranslation {

}

interface IState {
    minWidth: number;
}

class ProgressBar extends React.PureComponent<IPropsExtended, IState> {
    static defaultProps: Partial<IProps> = {
        descriptionFlexDirection: "row",
        color: ProgressBarColor.Default
    };

    canvasRef = React.createRef<HTMLCanvasElement>();
    descriptionRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        if (this.props.isSkeleton) {
            return;
        }

        if (this.canvasRef.current) {
            this.canvasRef.current.width = this.canvasRef.current.parentElement.offsetWidth;
        }

        this.renderCanvas();
        this.calcMinWidth();
    }

    componentDidUpdate(prevProps: Readonly<IPropsExtended>, prevState: Readonly<{}>, snapshot?: any) {
        if (this.props.isSkeleton) {
            return;
        }

        this.renderCanvas();
        if (this.props.customDescription !== prevProps.customDescription
            || this.props.descWidthRatio !== prevProps.descWidthRatio
            || !this.state?.minWidth) {
            this.calcMinWidth();
        }
    }

    get showNumbers() {
        return !this.props.hideNumbers && this.props.parts > 0;
    }

    get description() {
        return this.props.customDescription ?? this.props.t(`Components:ProgressBar.${this.showNumbers ? "Remaining" : "InProgress"}`);
    }

    get minWidth() {
        // we consider full description, e.g. "Completed 215/215". It might not be the widest
        // option, but it's a good approximation. Minimum ratio adds some safety margin.
        const fullDescription = `${this.description} ${this.props.parts}/${this.props.parts}`;
        const minRatio = 1.2;
        const ratio = Math.max(this.props.descWidthRatio ?? 0, minRatio);
        const el = this.descriptionRef.current?.querySelector("span");
        if (!el) {
            return null;
        }
        const calcWidth = getTextWidthByElement(fullDescription, el);
        return calcWidth * ratio;
    }

    calcMinWidth() {
        const minWidth = this.minWidth;
        if (minWidth) {
            this.setState({ minWidth });
        }
    }

    renderCanvas = () => {
        if (!this.props.showMarks) {
            return;
        }

        const ctx = this.canvasRef.current.getContext("2d");
        const width = this.canvasRef.current.width;
        const partWidth = width / this.props.parts;

        ctx.clearRect(0, 0, this.canvasRef.current.width, this.canvasRef.current.height);

        for (let i = 1; i < this.props.parts; i++) {
            let color = "transparent";

            if (i < this.props.value) {
                color = this.props.theme.C_ACT_light_line;
            } else if (i > this.props.value) {
                color = transparentize(0.5, this.props.theme.C_ACT_main);
            }

            ctx.fillStyle = color;
            ctx.fillRect(i * partWidth - 1, 0, 1, 8);
        }

    };

    getPercentage = () => {
        if (this.props.parts === 0) {
            return 100;
        }

        return this.props.value * 100 / this.props.parts;
    };

    renderDescription = () => {
        if (this.props.hideDescription) {
            return null;
        }

        const { parts, value } = this.props;
        const calcValue = roundToDecimalPlaces(0, this.props.isDescriptionInverseValue ? parts - value : value);

        if (this.props.descriptionFormatter) {
            const description = this.props.descriptionFormatter(calcValue, this.props.parts);
            return typeof description === "string" ? <span>{description}</span> : description;
        }

        return (
            <>
                <span>{this.description}&nbsp;</span>
                {this.showNumbers && <span>{`${calcValue}/${parts}`}</span>}
            </>
        );
    };

    render() {
        if (this.props.isSkeleton) {
            return <ProgressBarSkeleton/>;
        }


        return (
            <StyledProgressBar data-testid={TestIds.ProgressBar}
                               style={this.props.style}
                               $minWidth={this.state?.minWidth}
                               className={this.props.className}>
                <ProgressWrapper $color={this.props.color}
                                 $animated={this.props.animateEmpty && this.props.value === 0}>
                    <ProgressValue data-testid={TestIds.ProgressValue}
                                   $value={this.getPercentage()}
                                   $animated={this.props.animateEmpty && this.props.value === 0}
                                   $color={this.props.color}/>
                    {this.props.showMarks &&
                        <StyledCanvas ref={this.canvasRef}/>
                    }
                </ProgressWrapper>
                <Description data-testid={TestIds.ProgressBarDescription}
                             ref={this.descriptionRef}
                             flexDirection={this.props.descriptionFlexDirection}>
                    {this.renderDescription()}
                </Description>

            </StyledProgressBar>
        );
    }
}

export default withTranslation(["Components"])(withTheme(ProgressBar));

const ProgressBarSkeleton: React.FunctionComponent = () => {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "center" }}>
            <Skeleton width={"340px"} height={"8px"} borderRadius={"4px"}/>
            <Skeleton width={"120px"} height={"16px"}/>
        </div>
    );
};